import { useCallback } from 'react';
import { gql } from '@apollo/client';
import map from 'lodash/map';
import every from 'lodash/every';
import some from 'lodash/some';
import omitBy from 'lodash/omitBy';
import omit from 'lodash/omit';

import type { NameVariation, CarAdditionalInfo } from '@jerry-serverless/types';
import type { UpdatePolicyInfoSteps } from '@jerry-serverless/enums/signup';

import { getClient, parseError } from '../index';
import type { Gender, MaritalStatus, RelationToUser } from '../../types/driver';
import type { CarUsage, OwnershipEnum } from '../../types/car';
import type { Education } from '../../types/education.enum';
import type { PolicyStatus } from '../../types/policy_status.enum';
import type { PolicyItem, SearchPolicyCar, SearchPolicyDriver } from '../../types/policy';
import { getCarAndMaintenanceReminderDataForMutation } from '../../utils/car';
import type { ViolationType } from '../../types/violation';
import type { MaintenanceReminderDataType } from '../../types/app_data';

import type { User } from './user.fragment';
import { USER_FRAGMENT } from './user.fragment';

/**
 * Update policy info mutation.
 */
export const UPDATE_POLICY_INFO_MUTATION = gql`
  mutation UpdatePolicyInfo($input: UpdatePolicyInfoInput!) {
    updatePolicyInfo(input: $input) {
      user {
        ${USER_FRAGMENT}
      }
    }
  }
`;

export interface UpdatePolicyInfoInput {
  /**
   * Whether policy items came from predicted coverage.
   */
  isPredicted: boolean | null;
  /**
   * Whether policy items coming from predicted coverage were edited.
   */
  predictedEdited: boolean | null;
  /**
   * Whether to show the quotes for this policy as `custom` tier.
   */
  useCustomPolicy: boolean | null;
  policyStatus: PolicyStatus | null;
  policyCarrier: string | null;
  drivers: {
    id: string;
    name: string;
    dateOfBirth?: string;
    gender?: Gender;
    maritalStatus?: MaritalStatus;
    relationToUser?: RelationToUser;
    occupation?: string;
    industry?: string;
    education?: Education;
    additionalInfo?: NameVariation;
  }[];
  cars: {
    id: string;
    year: string;
    make?: string;
    model?: string;
    submodel?: string;
    engine?: string;
    vin?: string;
    isPartialVin?: boolean;
    usage?: CarUsage;
    annualMileage?: number;
    rideSharing?: boolean;
    ownership?: OwnershipEnum;
    userAdded?: boolean;
    licensePlateState?: string;
    licensePlateNumber?: string;
    additionalInfo?: CarAdditionalInfo;
  }[];
  coverages: PolicyItem[];
  updateFields?: string[];
  step?: UpdatePolicyInfoSteps;
}

export interface UpdatePolicyInfoInputVariables {
  input: UpdatePolicyInfoInput;
}

export interface UpdatePolicyInfoData {
  updatePolicyInfo: {
    user: User | null;
  };
}

/** Convert searchPolicyDrivers to drivers input */
export function searchPolicyDriversToDriversInput({
  drivers,
  driverViolations,
}: {
  drivers: SearchPolicyDriver[];
  driverViolations: { [key: string]: ViolationType[] } | undefined;
}): UpdatePolicyInfoInput['drivers'] {
  return map(drivers, (driver) => ({
    id: driver.id,
    name: driver.name,
    ...omitBy(
      {
        dateOfBirth: driver.dateOfBirth || undefined,
        ageLicensed: driver.ageLicensed || undefined,
        gender: driver.gender || undefined,
        // No default of 'undefined' for 'maritalStatus' since we need to reset 'maritalStatus' to 'null'.
        maritalStatus: driver.maritalStatus,
        relationToUser: driver.relationToUser || undefined,
        occupation: driver.occupation || undefined,
        industry: driver.industry || undefined,
        education: driver.education || undefined,
        violations: driverViolations?.[driver.id] || undefined,
        driverLicenseStatus: driver.driverLicenseStatus || undefined,
        rated: driver.rated || undefined,
        excludedReason: driver.excludedReason || undefined,
        additionalInfo: driver.additionalInfo ? omit(driver.additionalInfo, '__typename') : null,
        driverLicenseNumber: driver.driverLicenseNumber,
        driverLicenseState: driver.driverLicenseState,
      },
      (attrib) => attrib === undefined
    ),
  }));
}

type UpdatePolicyInfoFnType = (value: {
  cars: SearchPolicyCar[];
  maintenanceReminderData: Record<string, MaintenanceReminderDataType> | undefined;
  MaintenanceReminderSettledCars: Set<string> | undefined;
  drivers: SearchPolicyDriver[];
  driverViolations: { [key: string]: ViolationType[] } | undefined;
  policyItems: PolicyItem[] | undefined;
  options: Omit<UpdatePolicyInfoInput, 'cars' | 'drivers' | 'coverages'>;
  updateFields?: string[];
}) => Promise<{ error?: string | undefined; user?: User | null }>;

/**
 * Hook for Update policy info.
 *
 * @param cars
 * @param drivers
 * @param policyItems
 * @param options
 */
export const useUpdatePolicyInfo = () => {
  const client = getClient();

  return useCallback<UpdatePolicyInfoFnType>(
    async ({
      cars,
      maintenanceReminderData,
      MaintenanceReminderSettledCars,
      drivers,
      driverViolations,
      policyItems,
      options,
      updateFields,
    }) => {
      try {
        let coverages = policyItems || [];
        // If we don't have policy items for each of the cars then we need to zap them all.
        if (!every(cars, (car) => some(policyItems, (item) => item.assetID === car.id))) {
          coverages = [];
        }

        // Assemble update policy input data.
        const input: UpdatePolicyInfoInputVariables['input'] = {
          ...options,
          coverages,
          cars: getCarAndMaintenanceReminderDataForMutation({
            cars,
            maintenanceReminderData,
            MaintenanceReminderSettledCars,
          }),
          drivers: searchPolicyDriversToDriversInput({ drivers, driverViolations }),
          updateFields,
        };

        const resp = await client.mutate<UpdatePolicyInfoData, UpdatePolicyInfoInputVariables>({
          mutation: UPDATE_POLICY_INFO_MUTATION,
          variables: { input },
        });

        return { user: resp.data?.updatePolicyInfo.user || null };
      } catch (err) {
        return { error: parseError(err) };
      }
    },
    [client]
  );
};
